"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Phone,
  Send,
  Building2,
  ShieldCheck,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BRAND } from "@/lib/brand";
import { readAttribution } from "@/lib/attribution";
import { Turnstile } from "@/components/security/turnstile";

/* -------------------------------------------------------------------------- */
/*  Schema — separate from the emergency contact form because the audience    */
/*  and intent are different. PMs scheduling a property walk shouldn't see    */
/*  emergency-shaped fields ("loss type", "active emergency boolean") that    */
/*  imply an active loss when they're explicitly here for non-emergency       */
/*  preventive program inquiry.                                                */
/* -------------------------------------------------------------------------- */
const PROPERTY_TYPES = [
  { value: "single_commercial", label: "Single commercial building" },
  { value: "multi_property", label: "Multi-property portfolio" },
  { value: "hospitality", label: "Hospitality (hotel / resort / restaurant)" },
  { value: "healthcare", label: "Healthcare facility" },
  { value: "hoa", label: "HOA / community association" },
  { value: "industrial", label: "Industrial / manufacturing" },
  { value: "mixed", label: "Mixed portfolio" },
  { value: "other", label: "Other" },
] as const;

const PORTFOLIO_SIZES = [
  { value: "single", label: "1 property" },
  { value: "small_portfolio_2_5", label: "2–5 properties" },
  { value: "mid_portfolio_6_10", label: "6–10 properties" },
  { value: "large_portfolio_11_plus", label: "11+ properties" },
  { value: "enterprise", label: "Enterprise (multi-site)" },
] as const;

const schema = z.object({
  name: z.string().min(2, "Please enter your name").max(120),
  title: z.string().max(120).optional().or(z.literal("")),
  company: z
    .string()
    .min(2, "Please enter your company / property management firm")
    .max(160),
  phone: z
    .string()
    .min(7, "Phone number looks too short")
    .max(32, "Phone number looks too long"),
  email: z.string().email("Enter a valid work email"),
  propertyType: z.enum([
    "single_commercial",
    "multi_property",
    "hospitality",
    "healthcare",
    "hoa",
    "industrial",
    "mixed",
    "other",
  ]),
  portfolioSize: z.enum([
    "single",
    "small_portfolio_2_5",
    "mid_portfolio_6_10",
    "large_portfolio_11_plus",
    "enterprise",
  ]),
  propertyLocation: z
    .string()
    .min(2, "Please enter the property city or ZIP")
    .max(120),
  currentVendor: z.string().max(160).optional().or(z.literal("")),
  preferredContactMethod: z.enum(["email", "phone", "either"]),
  notes: z.string().max(4000).optional().or(z.literal("")),
  // Honeypot — visually hidden.
  website: z.string().max(0, "caught").optional().or(z.literal("")),
});
type FormValues = z.infer<typeof schema>;

/* -------------------------------------------------------------------------- */
/*  Phone mask — same progressive formatter as /contact.                       */
/* -------------------------------------------------------------------------- */
function formatPhone(raw: string): string {
  if (!raw) return "";
  if (raw.trim().startsWith("+")) return raw;
  const digits = raw.replace(/\D/g, "");
  const ten =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length === 0) return "";
  if (ten.length < 4) return `(${ten}`;
  if (ten.length < 7) return `(${ten.slice(0, 3)}) ${ten.slice(3)}`;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6, 10)}`;
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */
export default function ScheduleWalkPage() {
  const router = useRouter();
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [utm, setUtm] = useState<string>("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") setUtm(window.location.search ?? "");
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      title: "",
      company: "",
      phone: "",
      email: "",
      propertyType: "single_commercial",
      portfolioSize: "single",
      propertyLocation: "",
      currentVendor: "",
      preferredContactMethod: "either",
      notes: "",
      website: "",
    },
  });

  async function onSubmit(values: FormValues) {
    setServerError(null);
    try {
      const attribution = readAttribution();
      const res = await fetch("/api/dispatch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: "preventive_walk",
          ...values,
          utm,
          attribution,
          turnstileToken,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setServerError(
          data?.message ??
            `We couldn't deliver your request. Please call ${BRAND.phone} or email ${BRAND.email} directly.`,
        );
        return;
      }
      // Fire GA4 conversion event so paid ads can optimize toward
      // preventive-care leads as a distinct conversion type.
      try {
        const w = window as unknown as { gtag?: (...args: unknown[]) => void };
        if (typeof w.gtag === "function") {
          w.gtag("event", "generate_lead", {
            form_name: "DRR Preventive Care Walk Request",
            source: "website",
            lead_type: "preventive_care_walk",
          });
          const adsId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
          const adsLabel = process.env.NEXT_PUBLIC_GOOGLE_ADS_LEAD_LABEL;
          if (adsId && adsLabel) {
            w.gtag("event", "conversion", {
              send_to: `${adsId}/${adsLabel}`,
              // Higher value than emergency lead — recurring B2B contracts
              // are dramatically more valuable than one-off losses. Tune
              // upward once Eduardo has real LTV data.
              value: 1500,
              currency: "USD",
            });
          }
        }
      } catch {
        // Analytics must never break the success path.
      }
      setSubmitted(true);
      router.push("/thank-you?lt=preventive_walk&em=0");
    } catch {
      setServerError(
        `Network error. Please call ${BRAND.phone} or email ${BRAND.email} directly.`,
      );
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundImage:
          "url('/images/arizona-landmarks/sedona-cathedral-rock-arizona.jpg')",
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
        backgroundPosition: "center",
      }}
    >
      <section
        style={{
          padding: "120px 24px 80px",
          background: "rgba(5,13,24,0.55)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            maxWidth: 880,
            margin: "0 auto",
            background: "rgba(5,13,24,0.78)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: 24,
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "0 4px 32px rgba(0,0,0,0.5)",
            padding: "56px 48px",
          }}
        >
          {/* Header */}
          <div
            className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest"
            style={{ color: "#D4A84B" }}
          >
            <Calendar className="size-3.5" /> Preventive Care · Property Walk
          </div>
          <h1
            className="mt-4 text-4xl font-bold tracking-tight md:text-5xl"
            style={{
              color: "#ffffff",
              fontWeight: 800,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            Schedule a property walk.
          </h1>
          <p
            className="mt-3 max-w-2xl"
            style={{
              color: "rgba(255,255,255,0.82)",
              fontFamily: '"Barlow", sans-serif',
              fontSize: "1.05rem",
              lineHeight: 1.65,
            }}
          >
            Tell us a bit about the property. A DRR project manager will reach
            out within one business day to schedule a 60–90 minute on-site walk.
            No cost. No contract pressure on the walk itself — about half of
            walks become contracts, the other half become reactive emergency
            relationships that convert later.
          </p>

          {submitted ? (
            <div className="mt-10 rounded-2xl border border-emerald-500/40 bg-transparent p-8">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 size-6 shrink-0 text-emerald-600" />
                <div>
                  <h2
                    className="text-xl font-bold"
                    style={{ color: "#ffffff" }}
                  >
                    Request received.
                  </h2>
                  <p
                    className="mt-2 text-sm"
                    style={{ color: "rgba(255,255,255,0.82)" }}
                  >
                    A DRR project manager will reach out within one business
                    day to schedule the walk. Active emergency in the meantime?{" "}
                    <a
                      href={BRAND.phoneHref}
                      className="font-bold underline underline-offset-2"
                      style={{ color: "#D4A84B" }}
                    >
                      {BRAND.phone}
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className="mt-10 space-y-6 rounded-2xl p-6 md:p-8"
              style={{
                background: "rgba(5,13,24,0.6)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              {/* Honeypot */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden"
              >
                <label htmlFor="website-walk">Website (leave blank)</label>
                <input
                  id="website-walk"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  {...register("website")}
                />
              </div>

              {/* Contact */}
              <div className="grid gap-5 md:grid-cols-2">
                <Field id="name" label="Full Name" error={errors.name?.message}>
                  <Input
                    id="name"
                    className="h-11 border-white/20 bg-transparent placeholder:text-gray-300"
                    style={{
                      color: "#ffffff",
                      backgroundColor: "rgba(255,255,255,0.07)",
                    }}
                    placeholder="Jamie Hernandez"
                    autoComplete="name"
                    {...register("name")}
                  />
                </Field>
                <Field
                  id="title"
                  label="Job Title"
                  optional
                  error={errors.title?.message}
                >
                  <Input
                    id="title"
                    className="h-11 border-white/20 bg-transparent placeholder:text-gray-300"
                    style={{
                      color: "#ffffff",
                      backgroundColor: "rgba(255,255,255,0.07)",
                    }}
                    placeholder="Director of Operations"
                    autoComplete="organization-title"
                    {...register("title")}
                  />
                </Field>
                <Field
                  id="company"
                  label="Company / Property Management Firm"
                  error={errors.company?.message}
                >
                  <Input
                    id="company"
                    className="h-11 border-white/20 bg-transparent placeholder:text-gray-300"
                    style={{
                      color: "#ffffff",
                      backgroundColor: "rgba(255,255,255,0.07)",
                    }}
                    placeholder="Acme Property Management LLC"
                    autoComplete="organization"
                    {...register("company")}
                  />
                </Field>
                <Field
                  id="phone"
                  label="Phone"
                  error={errors.phone?.message}
                >
                  {(() => {
                    const phoneReg = register("phone");
                    return (
                      <Input
                        id="phone"
                        type="tel"
                        inputMode="numeric"
                        maxLength={20}
                        className="h-11 border-white/20 bg-transparent placeholder:text-gray-300"
                        style={{
                          color: "#ffffff",
                          backgroundColor: "rgba(255,255,255,0.07)",
                        }}
                        placeholder="(602) 555-0123"
                        autoComplete="tel"
                        {...phoneReg}
                        onChange={(e) => {
                          const formatted = formatPhone(e.target.value);
                          e.target.value = formatted;
                          phoneReg.onChange(e);
                        }}
                      />
                    );
                  })()}
                </Field>
                <div className="md:col-span-2">
                  <Field
                    id="email"
                    label="Work Email"
                    error={errors.email?.message}
                  >
                    <Input
                      id="email"
                      type="email"
                      className="h-11 border-white/20 bg-transparent placeholder:text-gray-300"
                      style={{
                        color: "#ffffff",
                        backgroundColor: "rgba(255,255,255,0.07)",
                      }}
                      placeholder="jamie@acmepm.com"
                      autoComplete="email"
                      {...register("email")}
                    />
                  </Field>
                </div>
              </div>

              {/* Property */}
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 18,
                  borderTop: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <h2
                  style={{
                    color: "#D4A84B",
                    fontFamily: '"DM Mono", monospace',
                    fontSize: "11px",
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Building2 className="size-3.5" /> About the Property
                </h2>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                <Field
                  id="propertyType"
                  label="Property Type"
                  error={errors.propertyType?.message}
                >
                  <select
                    id="propertyType"
                    className="h-11 w-full rounded-md border border-white/20 px-3 text-sm"
                    style={{
                      color: "#ffffff",
                      backgroundColor: "rgba(255,255,255,0.07)",
                    }}
                    {...register("propertyType")}
                  >
                    {PROPERTY_TYPES.map((p) => (
                      <option
                        key={p.value}
                        value={p.value}
                        style={{ color: "#080F1A" }}
                      >
                        {p.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  id="portfolioSize"
                  label="Portfolio Size"
                  error={errors.portfolioSize?.message}
                >
                  <select
                    id="portfolioSize"
                    className="h-11 w-full rounded-md border border-white/20 px-3 text-sm"
                    style={{
                      color: "#ffffff",
                      backgroundColor: "rgba(255,255,255,0.07)",
                    }}
                    {...register("portfolioSize")}
                  >
                    {PORTFOLIO_SIZES.map((p) => (
                      <option
                        key={p.value}
                        value={p.value}
                        style={{ color: "#080F1A" }}
                      >
                        {p.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field
                    id="propertyLocation"
                    label="Property Location (City or ZIP)"
                    error={errors.propertyLocation?.message}
                  >
                    <Input
                      id="propertyLocation"
                      className="h-11 border-white/20 bg-transparent placeholder:text-gray-300"
                      style={{
                        color: "#ffffff",
                        backgroundColor: "rgba(255,255,255,0.07)",
                      }}
                      placeholder="Phoenix · 85004"
                      {...register("propertyLocation")}
                    />
                  </Field>
                </div>
                <Field
                  id="currentVendor"
                  label="Current Restoration Vendor"
                  optional
                  error={errors.currentVendor?.message}
                >
                  <Input
                    id="currentVendor"
                    className="h-11 border-white/20 bg-transparent placeholder:text-gray-300"
                    style={{
                      color: "#ffffff",
                      backgroundColor: "rgba(255,255,255,0.07)",
                    }}
                    placeholder="None / case-by-case / [vendor name]"
                    {...register("currentVendor")}
                  />
                </Field>
                <Field
                  id="preferredContactMethod"
                  label="Preferred Contact Method"
                  error={errors.preferredContactMethod?.message}
                >
                  <select
                    id="preferredContactMethod"
                    className="h-11 w-full rounded-md border border-white/20 px-3 text-sm"
                    style={{
                      color: "#ffffff",
                      backgroundColor: "rgba(255,255,255,0.07)",
                    }}
                    {...register("preferredContactMethod")}
                  >
                    <option value="either" style={{ color: "#080F1A" }}>
                      Either email or phone
                    </option>
                    <option value="email" style={{ color: "#080F1A" }}>
                      Email only
                    </option>
                    <option value="phone" style={{ color: "#080F1A" }}>
                      Phone only
                    </option>
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field
                    id="notes"
                    label="Anything specific you want us to look at?"
                    optional
                    error={errors.notes?.message}
                  >
                    <textarea
                      id="notes"
                      rows={4}
                      className="w-full rounded-md border border-white/20 px-3 py-2 text-sm"
                      style={{
                        color: "#ffffff",
                        backgroundColor: "rgba(255,255,255,0.07)",
                      }}
                      placeholder="Recurring leak in NE corner, recent monsoon damage, planning to renew property insurance and want documented baseline first…"
                      {...register("notes")}
                    />
                  </Field>
                </div>
              </div>

              {serverError && (
                <div className="flex items-start gap-3 rounded-xl border border-red-500/40 bg-transparent p-4">
                  <AlertTriangle className="mt-0.5 size-5 shrink-0 text-[#D4A84B]" />
                  <p className="text-sm" style={{ color: "#D4A84B" }}>
                    {serverError}
                  </p>
                </div>
              )}

              <Turnstile onToken={setTurnstileToken} />

              <div className="flex flex-col-reverse gap-4 border-t border-white/15 pt-6 md:flex-row md:items-center md:justify-between">
                <p
                  className="text-xs"
                  style={{ color: "rgba(255,255,255,0.70)" }}
                >
                  By submitting, you agree to be contacted by Disaster Recovery
                  Restoration LLC about your inquiry. We never sell your
                  contact information. See our{" "}
                  <a
                    href="/privacy"
                    className="underline hover:text-white"
                  >
                    privacy policy
                  </a>
                  .
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={cn(
                    buttonVariants({ size: "lg" }),
                    "h-12 gap-2 bg-[#D4A84B] text-base font-bold text-[#080F1A] hover:bg-[#c19a3f] disabled:opacity-50",
                  )}
                >
                  <Send className="size-4" />
                  {isSubmitting ? "Sending…" : "Request Property Walk"}
                </button>
              </div>
            </form>
          )}

          {/* Reassurance strip */}
          <div
            className="mt-10 grid gap-4 md:grid-cols-3"
            style={{ marginTop: 32 }}
          >
            {[
              {
                icon: ShieldCheck,
                tag: "60–90 minutes",
                body: "On-site assessment, no charge.",
              },
              {
                icon: Calendar,
                tag: "1 business day",
                body: "We reach back out to schedule.",
              },
              {
                icon: Phone,
                tag: "Active loss?",
                body: BRAND.phone + " is the dispatch line.",
              },
            ].map(({ icon: Icon, tag, body }) => (
              <div
                key={tag}
                style={{
                  background: "rgba(5,13,24,0.55)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 12,
                  padding: "1rem 1.1rem",
                }}
              >
                <Icon
                  size={18}
                  aria-hidden="true"
                  style={{ color: "#D4A84B", marginBottom: 8 }}
                />
                <p
                  style={{
                    color: "#D4A84B",
                    fontFamily: '"DM Mono", monospace',
                    fontSize: "10px",
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 4,
                  }}
                >
                  {tag}
                </p>
                <p
                  style={{
                    color: "rgba(255,255,255,0.82)",
                    fontFamily: '"Barlow", sans-serif',
                    fontSize: "0.85rem",
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Local Field wrapper — same shape as /contact for visual consistency       */
/* -------------------------------------------------------------------------- */
function Field({
  id,
  label,
  optional,
  error,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={id}
        className="text-xs font-semibold uppercase tracking-widest"
        style={{ color: "rgba(255,255,255,0.82)" }}
      >
        {label}{" "}
        {optional ? (
          <span style={{ color: "rgba(255,255,255,0.45)" }}>(optional)</span>
        ) : null}
      </Label>
      {children}
      {error ? (
        <p className="text-xs" style={{ color: "#D4A84B" }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
